import { cn } from '@seen/ui'
import { cva } from 'class-variance-authority'

// Hide remote users' caret labels while the local editor is focused so they don't obscure where
// the user is typing. We can't detect when a remote label sits over the local caret, so we hide
// them all. Vendor class from @liveblocks/react-tiptap's CollaborationCaret extension
// (collaboration-caret.js → `label.classList.add("collaboration-carets__label")`); was
// `collaboration-cursor__label` before the Liveblocks 3.10 / Tiptap v3 migration, so re-check on
// the next vendor upgrade if labels stop hiding on focus.
export const hideRemoteCollaborationCaretLabelOnFocus = String.raw`[&.ProseMirror-focused_.collaboration-carets\_\_label]:hidden`

export const tiptapVariants = cva(
  [
    // Blockquote styling
    cn(
      '0 282.863)] [&_blockquote]:relative [&_blockquote]:mt-4 [&_blockquote]:pl-4 [&_blockquote]:before:absolute [&_blockquote]:before:top-0 [&_blockquote]:before:bottom-0 [&_blockquote]:before:left-0 [&_blockquote]:before:w-1 [&_blockquote]:before:rounded-full [&_blockquote]:before:bg-[lch(86.5 [&_blockquote]:before:bg-border'
    ),
  ],
  {
    variants: {
      noHeadings: {
        true: null,
        false: [
          // Downsize the headings
          '[&_h1]:font-sans [&_h1]:font-semibold [&_h1]:text-3xl',
          '[&_h2]:font-semibold [&_h2]:text-lg',
          '[&_h3]:font-semibold [&_h3]:text-base [&_h3]:leading-6',
        ],
      },
      noLists: {
        true: null,
        false: [
          [
            // Bullets UL styling
            '[&_ul:not([data-type="taskList"])_li]:ml-6',
            '[&_li_>_ul:not([data-type="taskList"])]:ml-3',
            '[&_ul:not([data-type="taskList"])_li_>_p]:ml-3',
            '[&_ul:not([data-type="taskList"])_li]:marker:text-foreground-secondary',
            '[&_ul:not([data-type="taskList"])]:list-disc',
            '[&_ul:not([data-type="taskList"])_ul]:list-[circle]',
            '[&_ul:not([data-type="taskList"])_ul_ul]:list-disc',
            '[&_ul:not([data-type="taskList"])_ul_ul_ul]:list-[circle]',
          ],
          [
            // Checkbox UL styling
            '[&_ul[data-type="taskList"]_li]:flex',
            '[&_ul[data-type="taskList"]_li>label>input]:ml-2',
            '[&_ul[data-type="taskList"]_li>label]:w-9',
            '[&_ul[data-type="taskList"]_li>label]:shrink-0',
            '[&_ul[data-type="taskList"]_li>label]:flex',
            '[&_ul[data-type="taskList"]_li>label]:items-center',
          ],
          '[&_li_>_ol]:ml-3 [&_ol]:list-decimal [&_ol_li]:ml-6 [&_ol_li]:marker:text-foreground-secondary [&_ol_li_>_p]:ml-3 [&_ol_ol]:list-[lower-alpha] [&_ol_ol_ol]:list-[lower-roman]', // OL styling
        ],
      },
      noPlaceholder: {
        true: null,
        false: [
          '[&_p.is-editor-empty]:first:before:pointer-events-none [&_p.is-editor-empty]:first:before:float-left [&_p.is-editor-empty]:first:before:h-0 [&_p.is-editor-empty]:first:before:opacity-50 [&_p.is-editor-empty]:first:before:content-[attr(data-placeholder)]', // https://tiptap.dev/docs/editor/extensions/functionality/placeholder#placeholder
        ],
      },
    },
    defaultVariants: {
      noHeadings: false,
      noLists: false,
      noPlaceholder: false,
    },
  }
)
